// src/components/sat/PlanSummaryCard.js
//
// One clean, balanced summary header used at the top of the plan detail view
// for both SAT and ACT. Three intentional zones in a single card:
//   1) Completion gauge (overall progress, the focal point)
//   2) Score goal (current → target, gap) + hour tiles (planned/covered/remaining)
//   3) Hour-allocation donut by subject/section
// Plus a top strip with test type, status, diagnostic source, and date.

import React from "react";
import { summarizePlanProgress, derivePlanDisplayStatus, tsToMs } from "../../utils/sat/satPlanData";
import { planTypeOf, planSubjectOrder, PLAN_TYPES, isCustomPlan } from "../../utils/plan/planConfig";
import HourDonut, { DonutLegend, colorForSubject } from "./HourDonut";
import ProgressGauge from "./ProgressGauge";

const STATUS = {
  active: { label: "Active", bg: "#dcfce7", color: "#166534" },
  completed: { label: "Completed", bg: "#dbeafe", color: "#1e40af" },
  draft: { label: "Draft", bg: "#f1f5f9", color: "#475569" },
  archived: { label: "Archived", bg: "#f1f5f9", color: "#94a3b8" },
};

function fmtTs(ts) {
  const ms = tsToMs(ts);
  if (!ms) return "—";
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

export default function PlanSummaryCard({ plan }) {
  const type = planTypeOf(plan);
  const label = PLAN_TYPES[type].label;
  const custom = isCustomPlan(plan);
  const status = derivePlanDisplayStatus(plan);
  const badge = STATUS[status] || STATUS.draft;
  const prog = summarizePlanProgress(plan);

  const byS = {};
  (plan.checklist || [])
    .filter((i) => !i.isStretch)
    .forEach((i) => {
      byS[i.subject] = (byS[i.subject] || 0) + (Number(i.allocatedHours) || 0);
    });
  const segs = planSubjectOrder(plan).map((s, i) => ({
    label: s,
    value: byS[s] || 0,
    color: colorForSubject(s, i),
  }));

  const current = plan.currentScore ?? plan.totalScore ?? "—";
  const target = plan.targetScore ?? "—";
  const gap = plan.scoreGap;

  return (
    <div style={st.card}>
      <div style={st.strip}>
        <div style={st.stripLeft}>
          <span style={st.typeChip}>{label}</span>
          <span style={{ ...st.badge, background: badge.bg, color: badge.color }}>{badge.label}</span>
        </div>
        <div style={st.stripRight}>
          {custom
            ? `${plan.title || "Custom plan"} · Created ${fmtTs(plan.createdAt)}`
            : `From ${plan.attemptLabel || `${label} diagnostic`} · Confirmed ${fmtTs(plan.confirmedAt || plan.createdAt)}`}
        </div>
      </div>

      <div style={st.body}>
        {/* Zone 1 — completion */}
        <div style={st.zoneGauge}>
          <ProgressGauge percent={prog.percent} size={104} stroke={11} />
          <div style={st.gaugeMeta}>
            <div style={st.gaugeMain}>{prog.doneCount}/{prog.requiredCount} items done</div>
            <div style={st.gaugeSub}>{prog.coveredHours}/{prog.plannedHours}h worked</div>
          </div>
        </div>

        <div style={st.vline} />

        {/* Zone 2 — score goal (SAT/ACT) or hour totals (custom) */}
        {custom ? (
          <div style={st.zoneScore}>
            <div style={st.zoneLabel}>Planned hours</div>
            <div style={st.scoreRow}>
              <span style={st.scoreBig}>{plan.totalHours || 0}h</span>
              <span style={st.arrow}>·</span>
              <span style={st.scoreBig}>{segs.length}</span>
              <span style={st.gapPill}>{segs.length === 1 ? "subject" : "subjects"}</span>
            </div>
            <div style={st.hourTiles}>
              <HourTile value={`${prog.plannedHours}h`} label="Planned" />
              <HourTile value={`${prog.coveredHours}h`} label="Covered" />
              <HourTile value={`${prog.remainingHours}h`} label="Remaining" />
            </div>
          </div>
        ) : (
          <div style={st.zoneScore}>
            <div style={st.zoneLabel}>Score goal</div>
            <div style={st.scoreRow}>
              <span style={st.scoreBig}>{current}</span>
              <span style={st.arrow}>→</span>
              <span style={st.scoreBig}>{target}</span>
              {gap != null && <span style={st.gapPill}>+{gap}</span>}
            </div>
            <div style={st.hourTiles}>
              <HourTile value={`${prog.plannedHours}h`} label="Planned" />
              <HourTile value={`${prog.coveredHours}h`} label="Covered" />
              <HourTile value={`${prog.remainingHours}h`} label="Remaining" />
            </div>
          </div>
        )}

        <div style={st.vline} />

        {/* Zone 3 — allocation */}
        <div style={st.zoneDonut}>
          <div style={st.zoneLabel}>Hour allocation</div>
          <div style={st.donutWrap}>
            <HourDonut segments={segs} size={108} thickness={15} />
            <DonutLegend segments={segs} />
          </div>
        </div>
      </div>
    </div>
  );
}

function HourTile({ value, label }) {
  return (
    <div style={st.tile}>
      <div style={st.tileVal}>{value}</div>
      <div style={st.tileLabel}>{label}</div>
    </div>
  );
}

const st = {
  card: { border: "1px solid #e5e7eb", borderRadius: 16, background: "#fff", overflow: "hidden", marginBottom: 14 },
  strip: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" },
  stripLeft: { display: "flex", alignItems: "center", gap: 8 },
  typeChip: { fontSize: 11, fontWeight: 800, letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 6, background: "#0f172a", color: "#fff" },
  badge: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", padding: "2px 8px", borderRadius: 999 },
  stripRight: { fontSize: 12.5, color: "#64748b" },

  body: { display: "flex", alignItems: "center", gap: 24, padding: "20px 22px", flexWrap: "wrap" },
  vline: { width: 1, alignSelf: "stretch", background: "#eef2f7" },

  zoneGauge: { display: "flex", alignItems: "center", gap: 14, flexShrink: 0 },
  gaugeMeta: {},
  gaugeMain: { fontSize: 14, fontWeight: 800, color: "#0f172a" },
  gaugeSub: { fontSize: 12.5, color: "#64748b", marginTop: 3 },

  zoneScore: { flex: "1 1 240px", minWidth: 220 },
  zoneLabel: { fontSize: 10.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8", marginBottom: 8 },
  scoreRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 14 },
  scoreBig: { fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  arrow: { fontSize: 20, color: "#cbd5e1" },
  gapPill: { fontSize: 13, fontWeight: 800, background: "#dcfce7", color: "#166534", padding: "2px 10px", borderRadius: 999 },
  hourTiles: { display: "flex", gap: 10 },
  tile: { background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 10, padding: "8px 14px", minWidth: 72 },
  tileVal: { fontSize: 17, fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  tileLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "#94a3b8", marginTop: 5 },

  zoneDonut: { flexShrink: 0 },
  donutWrap: { display: "flex", alignItems: "center", gap: 16 },
};
